import type { StaticEntityType } from "@config";
import { cn } from "@lib/cn";
import { dialogStyles, useSlidesLibraryStore } from "@modules/slides-library/shared";
import { useAppDispatch } from "@store";
import { setMany, setPrompt } from "@store/ai-slides";
import { closeDialog } from "@store/dialogs";
import { IconArrowRight, IconX } from "@tabler/icons-react";
import { useState } from "react";
import Popover from "@/components/ui/popover";
import {
  LAYOUT_THUMBNAILS,
  LayoutThumbnail,
  type LayoutThumbnailId,
} from "@/components/ui/layout-thumbnails";
import type { TDesignSlide } from "@/shared/types";

const DEFAULT_THUMBNAIL_ID: LayoutThumbnailId = "thumb-1";

const GENERATE_WITH_AI_CUSTOM_PROMPT_DIALOG_ID =
  "generate-with-ai-custom-prompt";

type GenerateWithAiCustomPromptDialogProps = {
  design: TDesignSlide;
  slide: StaticEntityType | null;
  defaultValues: Record<string, unknown>;
};

const GenerateWithAiCustomPromptDialogInner = ({
  design,
  slide,
  defaultValues,
}: GenerateWithAiCustomPromptDialogProps) => {
  const [value, setValue] = useState("");
  const [selectedId, setSelectedId] = useState<LayoutThumbnailId>(DEFAULT_THUMBNAIL_ID);
  const dispatch = useAppDispatch();
  const view = useSlidesLibraryStore((s) => s.view);

  const hasPrompt = !!value?.trim();

  const handleClose = () =>
    dispatch(closeDialog(GENERATE_WITH_AI_CUSTOM_PROMPT_DIALOG_ID));

  const handleSubmit = () => {
    if (!hasPrompt) return;
    dispatch(setPrompt(value));
    dispatch(
      setMany({
        // NOT `designId`. That field means "go and fetch this design from the
        // API", and `selectedId` is a thumbnail from the picker above --
        // "thumb-1", not a design id. Sending it made the generator fetch
        // /api/slides/thumb-1/design, which 500s, so every "Add with AI" ended
        // in "Failed to generate ai slide". The design is right here, with the
        // slide the user clicked attached to it; there is nothing to fetch.
        design: {
          ...design,
          defaultData: defaultValues,
          slideData: slide as StaticEntityType,
        },
        status: "loading",
      }),
    );
    handleClose();
    if (view === "editor") {
      dispatch(closeDialog("slides-library"));
      dispatch(closeDialog("layouts-dialog"));
      dispatch(closeDialog("templates-dialog"));
    }
  };

  return (
    <div
      className={cn(
        dialogStyles.shadow,
        "tw-flex tw-h-[476px] tw-w-[768px] tw-flex-col tw-overflow-hidden tw-rounded-2xl tw-bg-white",
      )}
    >
      {/* Header */}
      <div className="tw-flex tw-h-16 tw-items-center tw-justify-between tw-border-b tw-border-solid tw-border-blue-100 tw-py-4 tw-pl-6 tw-pr-4">
        <span className="tw-font-sans tw-text-lg tw-font-semibold tw-text-blue">
          Generate with AI
        </span>
        <button
          type="button"
          onClick={handleClose}
          aria-label="Close"
          className="tw-flex tw-h-8 tw-w-8 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-border-0 tw-bg-transparent hover:tw-bg-blue-50"
        >
          <IconX size={16} stroke={1.5} className="tw-fill-none tw-text-blue" aria-hidden="true" />
        </button>
      </div>

      {/* Body */}
      <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-py-2">
        <div className="tw-px-6 tw-py-3">
          <span className="tw-font-sans tw-text-sm tw-font-medium tw-text-blue">
            Slide prompt
          </span>
        </div>

        <div className="tw-px-6 tw-pb-4">
          <div className="tw-relative tw-h-24 tw-rounded-lg tw-outline tw-outline-1 tw-outline-blue-100 !tw-outline-offset-0 tw-bg-white tw-p-3">
            <textarea
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey && hasPrompt) {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
              placeholder="Describe what you want to create..."
              aria-label="Slide prompt"
              className="tw-h-full tw-w-full tw-resize-none tw-border-0 tw-bg-transparent tw-pr-10 tw-font-sans tw-text-sm tw-leading-5 tw-text-blue tw-outline-none placeholder:tw-text-blue-500 [scrollbar-width:none] [&::-webkit-scrollbar]:tw-hidden"
            />
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!hasPrompt}
              aria-label="Generate slide"
              className={cn(
                "tw-absolute tw-bottom-3 tw-right-3 tw-flex tw-h-8 tw-w-8 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-shadow-2xs disabled:tw-cursor-not-allowed",
                hasPrompt
                  ? "tw-bg-primary hover:tw-bg-[#5209D9]"
                  : "tw-bg-blue-50",
              )}
            >
              <IconArrowRight
                size={16}
                stroke={1.5}
                aria-hidden="true"
                className={cn(
                  "tw-fill-none",
                  hasPrompt ? "tw-text-violet-50" : "tw-text-blue",
                )}
              />
            </button>
          </div>
        </div>

        <div className="tw-px-6 tw-py-3">
          <span className="tw-font-sans tw-text-sm tw-font-medium tw-text-blue">
            Choose layout
          </span>
        </div>

        <div className="tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-px-6 tw-pb-4">
          <div className="tw-grid tw-grid-cols-5 tw-gap-[12px]">
            {LAYOUT_THUMBNAILS.map(({ id }) => (
              <LayoutThumbnail
                key={id}
                id={id}
                selected={selectedId === id}
                onClick={() => setSelectedId(id)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export const GenerateWithAiCustomPromptDialog = Object.assign(
  GenerateWithAiCustomPromptDialogInner,
  { id: GENERATE_WITH_AI_CUSTOM_PROMPT_DIALOG_ID },
);

export const GenerateWithAiCustomPrompt = ({
  children,
  isOpen,
  setIsOpen,
  design,
  slide,
  defaultValues,
}: {
  children: React.ReactNode;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  design: TDesignSlide;
  slide: StaticEntityType | null;
  defaultValues: Record<string, unknown>;
}) => {
  const [value, setValue] = useState("");
  const dispatch = useAppDispatch();
  const view = useSlidesLibraryStore((s) => s.view);

  const hasPrompt = !!value?.trim();

  const handleSubmit = () => {
    if (!hasPrompt) return;
    dispatch(setPrompt(value));
    dispatch(
      setMany({
        design: {
          ...design,
          defaultData: defaultValues,
          slideData: slide as StaticEntityType,
        },
        status: "loading",
      }),
    );
    setIsOpen(false);
    if (view === "editor") {
      dispatch(closeDialog("slides-library"));
      dispatch(closeDialog("layouts-dialog"));
      dispatch(closeDialog("templates-dialog"));
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen} placement="bottom">
      <Popover.Trigger asChild>{children}</Popover.Trigger>
      <Popover.Content
        className={cn(
          dialogStyles.shadow,
          "tw-z-[10000] tw-flex tw-w-[458px] tw-flex-col tw-overflow-hidden tw-rounded-2xl tw-outline tw-outline-1 tw-outline-blue-100 !tw-outline-offset-0 tw-bg-white tw-p-0",
        )}
      >
        {/* Header */}
        <div className="tw-flex tw-items-center tw-justify-between tw-px-6 tw-pb-2 tw-pt-4">
          <span className="tw-font-sans tw-text-sm tw-font-medium tw-text-blue">
            Slide prompt
          </span>
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            aria-label="Close"
            className="tw-flex tw-h-8 tw-w-8 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-border-0 tw-bg-transparent hover:tw-bg-blue-50"
          >
            <IconX size={16} stroke={1.5} className="tw-fill-none tw-text-blue" aria-hidden="true" />
          </button>
        </div>

        {/* Body */}
        <div className="tw-px-6 tw-pb-4 tw-pt-2">
          <div className="tw-relative tw-h-24 tw-rounded-lg tw-outline tw-outline-1 tw-outline-blue-100 !tw-outline-offset-0 tw-bg-white tw-p-3">
            <textarea
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey && hasPrompt) {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
              placeholder="Describe what you want to create..."
              aria-label="Slide prompt"
              className="tw-h-full tw-w-full tw-resize-none tw-border-0 tw-bg-transparent tw-pr-10 tw-font-sans tw-text-sm tw-leading-5 tw-text-blue tw-outline-none placeholder:tw-text-blue-500 [scrollbar-width:none] [&::-webkit-scrollbar]:tw-hidden"
            />
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!hasPrompt}
              aria-label="Generate slide"
              className={cn(
                "tw-absolute tw-bottom-3 tw-right-3 tw-flex tw-h-8 tw-w-8 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-shadow-2xs disabled:tw-cursor-not-allowed",
                hasPrompt
                  ? "tw-bg-primary hover:tw-bg-[#5209D9]"
                  : "tw-bg-blue-50",
              )}
            >
              <IconArrowRight
                size={16}
                stroke={1.5}
                aria-hidden="true"
                className={cn(
                  "tw-fill-none",
                  hasPrompt ? "tw-text-violet-50" : "tw-text-blue",
                )}
              />
            </button>
          </div>
        </div>
      </Popover.Content>
    </Popover>
  );
};
